import { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { getStoredToken, storeToken, clearStoredToken, getRememberMePreference } from '../utils/tokenStorage';
import { startExpiryMonitor, stopExpiryMonitor } from '../utils/tokenExpiry';

interface User {
  id?: string;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  role?: string;
  tenant_id?: number;
  user_type?: string;
  consent_accepted_at?: string | null;
}

interface TenantBranding {
  name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  website_url?: string | null;
}

const DEFAULT_BRANDING: TenantBranding = {
  name: 'Adroit',
  logo_url: null,
  primary_color: '#0D9488',
  secondary_color: '#14B8A6',
  website_url: null
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  branding: TenantBranding;
  login: (email: string, password: string, subdomain?: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>;
  register: (username: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  registerGuest: (name: string, email: string, password: string, shareToken?: string) => Promise<{ success: boolean; error?: string; sharedFile?: { file_id: number; filename: string } }>;
  logout: () => void;
  setAuthFromToken: (token: string, userData: any) => void;
  markConsentAccepted: () => void;
  isAuthenticated: boolean;
  isGuest: boolean;
  getSubdomain: () => string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

const isIPv4Host = (hostname: string): boolean => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);

// Helper to get subdomain from hostname
const getSubdomain = (): string | null => {
  const hostname = window.location.hostname;
  if (!hostname || hostname === 'localhost' || isIPv4Host(hostname)) {
    return null;
  }
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0];
    if (subdomain !== 'app' && subdomain !== 'localhost' && subdomain !== 'www' && subdomain !== 'dev') {
      return subdomain;
    }
  }
  return null;
};

// Fetch tenant branding from API
const fetchTenantBranding = async (subdomain: string): Promise<TenantBranding> => {
  try {
    const response = await fetch(`/api/v1/tenants/by-subdomain/${subdomain}`);
    if (response.ok) {
      return await response.json();
    }
    // 404 is expected when no tenant record exists for this subdomain — fall back silently
  } catch {
    // Network errors — fall back silently
  }
  return DEFAULT_BRANDING;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [branding, setBranding] = useState<TenantBranding>(() => {
    // Restore tenant branding defensively so stale storage never breaks app boot.
    try {
      const saved = localStorage.getItem('tenantBranding');
      if (!saved) return DEFAULT_BRANDING;
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed.name === 'string') {
        return {
          name: parsed.name,
          logo_url: parsed.logo_url ?? null,
          primary_color: parsed.primary_color || DEFAULT_BRANDING.primary_color,
          secondary_color: parsed.secondary_color || DEFAULT_BRANDING.secondary_color,
          website_url: parsed.website_url ?? null,
        };
      }
    } catch {
      localStorage.removeItem('tenantBranding');
    }
    return DEFAULT_BRANDING;
  });

  // Fetch branding based on subdomain on mount
  useEffect(() => {
    const initBranding = async () => {
      const subdomain = getSubdomain();
      if (subdomain) {
        const tenantBranding = await fetchTenantBranding(subdomain);
        setBranding(tenantBranding);
        localStorage.setItem('tenantBranding', JSON.stringify(tenantBranding));
      }
    };
    initBranding();
  }, []);

  // Fetch user info on mount if we have a token
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = getStoredToken();

      if (!storedToken) {
        // No token - definitely not authenticated
        setToken(null);
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.getMe();
        // Validate that we got actual user data
        if (response.data && (response.data.email || response.data.username)) {
          setUser({
            id: response.data.id,
            email: response.data.email,
            username: response.data.username,
            full_name: response.data.full_name,
            first_name: response.data.full_name?.split(' ')[0],
            last_name: response.data.full_name?.split(' ').slice(1).join(' '),
            role: response.data.role,
            tenant_id: response.data.tenant_id,
            user_type: response.data.user_type || 'member',
            consent_accepted_at: response.data.consent_accepted_at || null,
          });
          setToken(storedToken);
          // Start session expiry monitor for restored session
          startExpiryMonitor(() => {
            console.warn('Session expires in 5 minutes');
          });
        } else {
          // Invalid response, clear auth
          console.error('Invalid user data from /me endpoint');
          clearStoredToken();
          setToken(null);
          setUser(null);
        }
      } catch (error) {
        // Token invalid, clear auth state
        console.error('Token validation failed:', error);
        clearStoredToken();
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []); // Only run once on mount

  const login = async (email: string, password: string, subdomain?: string, rememberMe?: boolean) => {
    try {
      // Auto-detect subdomain if not provided
      const effectiveSubdomain = subdomain ?? getSubdomain();
      const response = await authAPI.login(email, password, effectiveSubdomain ?? undefined);
      // v1 endpoint returns: { access_token, token_type, user }
      const { access_token, user: userData } = response.data;

      // Store token — localStorage if rememberMe, sessionStorage otherwise
      storeToken(access_token, rememberMe ?? false);
      setToken(access_token);

      // Start session expiry monitor — warns 5 min before expiry
      startExpiryMonitor(() => {
        console.warn('Session expires in 5 minutes');
      });

      // Set user from response
      if (userData) {
        setUser({
          id: userData.id,
          email: userData.email,
          username: userData.username,
          full_name: userData.full_name,
          first_name: userData.full_name?.split(' ')[0],
          last_name: userData.full_name?.split(' ').slice(1).join(' '),
          role: userData.role,
          tenant_id: userData.tenant_id,
          user_type: userData.user_type || 'member',
          consent_accepted_at: userData.consent_accepted_at || null,
        });
      } else {
        // Fallback to basic user info
        setUser({ email, username: email });
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const register = async (username: string, email: string, password: string) => {
    try {
      await authAPI.register(username, email, password);
      return await login(email, password);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  const registerGuest = async (name: string, email: string, password: string, shareToken?: string) => {
    try {
      const response = await fetch('/api/v1/auth/register-guest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, share_token: shareToken || null }),
      });
      if (!response.ok) {
        const err = await response.json();
        return { success: false, error: err.detail || 'Registration failed' };
      }
      const data = await response.json();
      // Store token and set user (guests use session storage)
      storeToken(data.token, false);
      setToken(data.token);
      setUser({
        id: data.user.id,
        email: data.user.email,
        username: data.user.username,
        full_name: data.user.full_name,
        first_name: data.user.full_name?.split(' ')[0],
        last_name: data.user.full_name?.split(' ').slice(1).join(' '),
        role: data.user.role,
        tenant_id: data.user.tenant_id,
        user_type: data.user.user_type || 'guest',
      });
      return {
        success: true,
        sharedFile: data.shared_file || undefined,
      };
    } catch (error: any) {
      return { success: false, error: 'Registration failed' };
    }
  };

  const setAuthFromToken = (newToken: string, userData: any) => {
    storeToken(newToken, getRememberMePreference());
    setToken(newToken);
    setUser({
      id: userData.id,
      email: userData.email,
      username: userData.username,
      full_name: userData.full_name,
      first_name: userData.full_name?.split(' ')[0],
      last_name: userData.full_name?.split(' ').slice(1).join(' '),
      role: userData.role,
      tenant_id: userData.tenant_id,
      user_type: userData.user_type || 'member',
    });
  };

  const markConsentAccepted = () => {
    setUser(prev => prev ? { ...prev, consent_accepted_at: new Date().toISOString() } : prev);
  };

  const logout = () => {
    stopExpiryMonitor();
    // Notify backend to invalidate session
    const storedToken = getStoredToken();
    if (storedToken) {
      fetch('/api/v1/auth/logout', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${storedToken}` },
      }).catch(() => {}); // Best-effort
    }
    clearStoredToken();
    localStorage.removeItem('tenantBranding');
    setToken(null);
    setUser(null);
    setBranding(DEFAULT_BRANDING);
  };

  // Check if user has actual data (not just empty object)
  const hasValidUser = user !== null && !!(user.email || user.username);

  const value = {
    user,
    token,
    loading,
    branding,
    login,
    register,
    registerGuest,
    logout,
    setAuthFromToken,
    markConsentAccepted,
    // Only authenticated if loading is complete AND we have valid token AND valid user
    isAuthenticated: !loading && !!token && hasValidUser,
    isGuest: user?.user_type === 'guest',
    getSubdomain,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
